import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  Search, 
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  User,
  X,
  BookOpen,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

const Schedules = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [createFormData, setCreateFormData] = useState({
    teacher: '',
    dayOfWeek: 'monday',
    startTime: '',
    endTime: '',
    primarySubject: '',
    maxStudents: 5,
    isAvailable: true,
    isRecurring: true,
    recurringDuration: '2weeks', // 2weeks, 1month, 2months, 3months, 6months
    oneTimeDates: [] // Array of specific dates for one-time schedules
  });
  const [editFormData, setEditFormData] = useState({});
  const [users, setUsers] = useState([]);
  
  useEffect(() => {
    fetchSchedules();
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [currentPage, searchTerm, dayFilter, teacherFilter, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(dayFilter && { dayOfWeek: dayFilter }),
        ...(teacherFilter && { teacher: teacherFilter })
      };

      const response = await api.get('/schedules', { params });
      console.log('Fetched schedules data:', response.data); // Debug log
      setSchedules(response.data.schedules);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Unified refresh helper
  const refreshSchedules = () => {
    fetchSchedules();
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule deleted successfully');
      refreshSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete schedule';
      toast.error(errorMessage);
    }
  };


  const handleViewSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setShowViewModal(true);
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    // Calculate duration from times if needed
    let calculatedDuration = 60;
    if (schedule.startTime && schedule.endTime) {
      const [startHour, startMin] = schedule.startTime.split(':').map(Number);
      const [endHour, endMin] = schedule.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      calculatedDuration = endMinutes - startMinutes;
    }
    
    setEditFormData({
      teacher: schedule.teacher?._id || '',
      dayOfWeek: schedule.dayOfWeek || 'monday',
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      primarySubject: schedule.primarySubject || '',
      maxStudents: schedule.maxStudents || 5,
      isAvailable: schedule.isAvailable !== undefined ? schedule.isAvailable : true,
      isRecurring: schedule.isRecurring !== undefined ? schedule.isRecurring : true,
      recurringDuration: schedule.isRecurring ? (schedule.effectiveUntil ? calculateRecurringDuration(schedule.effectiveFrom, schedule.effectiveUntil) : '2weeks') : '2weeks',
      oneTimeDates: schedule.specialDates?.map(sd => sd.date ? new Date(sd.date).toISOString().split('T')[0] : '') || []
    });
    setShowEditModal(true);
  };

  // Helper function to calculate recurring duration from dates
  const calculateRecurringDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '2weeks';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 21) return '2weeks';
    if (diffDays <= 45) return '1month';
    if (diffDays <= 75) return '2months';
    if (diffDays <= 105) return '3months';
    if (diffDays <= 200) return '6months';
    return '2weeks';
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    
    // Comprehensive validation - all fields required
    const validationErrors = [];
    
    if (!createFormData.teacher) validationErrors.push('Teacher is required');
    if (!createFormData.dayOfWeek) validationErrors.push('Day of week is required');
    if (!createFormData.startTime) validationErrors.push('Start time is required');
    if (!createFormData.endTime) validationErrors.push('End time is required');
    if (!createFormData.primarySubject) validationErrors.push('Primary subject is required');
    if (!createFormData.maxStudents || createFormData.maxStudents < 1) validationErrors.push('Max students is required (minimum 1)');
    
    // Validate schedule type selection
    if (createFormData.isRecurring === undefined && createFormData.isRecurring === null) {
      validationErrors.push('Schedule type (recurring or one-time) is required');
    }
    
    // Validate recurring schedule requirements
    if (createFormData.isRecurring === true) {
      if (!createFormData.recurringDuration) {
        validationErrors.push('Recurring duration is required for recurring schedules');
      }
    }
    
    // Validate one-time schedule requirements
    if (createFormData.isRecurring === false) {
      if (!createFormData.oneTimeDates || createFormData.oneTimeDates.length === 0) {
        validationErrors.push('At least one date is required for one-time schedules');
      } else {
        // Validate that all dates are filled
        const emptyDates = createFormData.oneTimeDates.filter(date => !date || date.trim() === '');
        if (emptyDates.length > 0) {
          validationErrors.push('All selected dates must have a valid date value');
        }
      }
    }
    
    // Validate time logic
    if (createFormData.startTime && createFormData.endTime) {
      const [startHour, startMin] = createFormData.startTime.split(':').map(Number);
      const [endHour, endMin] = createFormData.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (startMinutes >= endMinutes) {
        validationErrors.push('End time must be after start time');
      }
    }
    
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors: ${validationErrors.join(', ')}`);
      return;
    }
    
    // Calculate duration automatically from times
    const [startHour, startMin] = createFormData.startTime.split(':').map(Number);
    const [endHour, endMin] = createFormData.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const calculatedDuration = endMinutes - startMinutes;
    
    // Prepare data for API (exclude duration since it's calculated automatically)
    const scheduleData = {
      ...createFormData,
      duration: calculatedDuration,
      oneTimeDates: createFormData.isRecurring ? undefined : createFormData.oneTimeDates
    };
    
    // Remove undefined fields
    Object.keys(scheduleData).forEach(key => {
      if (scheduleData[key] === undefined) delete scheduleData[key];
    });
    
    console.log('Creating schedule with data:', scheduleData); // Debug log
    
    try {
      await api.post('/schedules', scheduleData);
      toast.success('Schedule created successfully');
      setShowCreateModal(false);
      setCreateFormData({
        teacher: '',
        dayOfWeek: 'monday',
        startTime: '',
        endTime: '',
        primarySubject: '',
        maxStudents: 5,
        isAvailable: true,
        isRecurring: true,
        recurringDuration: '2weeks',
        oneTimeDates: []
      });
      refreshSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to create schedule');
      }
    }
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    
    // Validate time logic
    if (editFormData.startTime && editFormData.endTime) {
      const [startHour, startMin] = editFormData.startTime.split(':').map(Number);
      const [endHour, endMin] = editFormData.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (startMinutes >= endMinutes) {
        toast.error('End time must be after start time');
        return;
      }
    }
    
    if (editFormData.isRecurring === false && (!editFormData.oneTimeDates || editFormData.oneTimeDates.length === 0)) {
      toast.error('Please select at least one date for one-time schedule');
      return;
    }
    
    // Calculate duration automatically
    let calculatedDuration = null;
    if (editFormData.startTime && editFormData.endTime) {
      const [startHour, startMin] = editFormData.startTime.split(':').map(Number);
      const [endHour, endMin] = editFormData.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      calculatedDuration = endMinutes - startMinutes;
    }
    
    // Prepare update data
    const updateData = {
      ...editFormData,
      duration: calculatedDuration,
      oneTimeDates: editFormData.isRecurring === false ? editFormData.oneTimeDates : undefined
    };
    
    // Remove undefined fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) delete updateData[key];
    });
    
    try {
      await api.put(`/schedules/${selectedSchedule._id}`, updateData);
      toast.success('Schedule updated successfully');
      setShowEditModal(false);
      setSelectedSchedule(null);
      refreshSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error(error.response?.data?.message || 'Failed to update schedule');
    }
  };

  const handleInputChange = (e, formType) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    if (formType === 'create') {
      setCreateFormData(prev => ({ ...prev, [name]: val }));
    } else if (formType === 'edit') {
      setEditFormData(prev => ({ ...prev, [name]: val }));
    }
  };


  const getDayBadge = (day) => {
    const dayConfig = {
      monday: { class: 'badge-primary', text: 'Monday' },
      tuesday: { class: 'badge-primary', text: 'Tuesday' },
      wednesday: { class: 'badge-primary', text: 'Wednesday' },
      thursday: { class: 'badge-primary', text: 'Thursday' },
      friday: { class: 'badge-primary', text: 'Friday' },
      saturday: { class: 'badge-warning', text: 'Saturday' },
      sunday: { class: 'badge-warning', text: 'Sunday' }
    };

    const config = dayConfig[day] || dayConfig.monday;
    return <span className={config.class}>{config.text}</span>;
  };

  const getAvailabilityBadge = (isAvailable) => {
    return isAvailable ? (
      <span className="badge badge-success">Available</span>
    ) : (
      <span className="badge badge-danger">Unavailable</span>
    );
  };

  // Convert 24-hour time (HH:MM) to 12-hour format (h:mm AM/PM)
  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const canManageSchedule = (schedule) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'teacher' && schedule.teacher?._id === user._id) return true;
    return false;
  };

  const getRoleSpecificUsers = (role) => {
    return users.filter(u => u.role === role && u.isActive);
  };

  if (!['admin', 'teacher', 'parent'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Clock className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedules</h1>
          <p className="text-gray-600">
            {user?.role === 'parent' 
              ? 'View available teacher schedules and book appointments' 
              : user?.role === 'teacher'
              ? 'Manage your availability and view student appointments'
              : 'Manage teacher availability and schedules'
            }
          </p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Schedule
          </button>
        )}
        {user?.role === 'parent' && (
          <Link
            to="/available-schedules"
            className="btn btn-primary"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Book Appointments
          </Link>
        )}
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search schedules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Day Filter */}
            <select
              value={dayFilter}
              onChange={(e) => setDayFilter(e.target.value)}
              className="input"
            >
              <option value="">All Days</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="sunday">Sunday</option>
            </select>

            {/* Teacher Filter */}
            <select
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
              className="input"
            >
              <option value="">All Teachers</option>
              {getRoleSpecificUsers('teacher').map(teacher => (
                <option key={teacher._id} value={teacher._id}>
                  {teacher.firstName} {teacher.lastName}
                </option>
              ))}
            </select>

            {/* Refresh Button */}
            <button
              onClick={refreshSchedules}
              className="btn btn-secondary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Schedules Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Schedules</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner h-8 w-8"></div>
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No schedules found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || dayFilter || teacherFilter ? 'Try adjusting your search criteria.' : 'Get started by creating a schedule.'}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Teacher</th>
                    <th className="table-header-cell">Day</th>
                    <th className="table-header-cell">Time</th>
                    <th className="table-header-cell">Subject</th>
                    <th className="table-header-cell">Capacity</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {schedules.map((schedule) => (
                    <tr key={schedule._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {schedule.teacher?.firstName} {schedule.teacher?.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {schedule.teacher?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        {getDayBadge(schedule.dayOfWeek)}
                      </td>
                      <td className="table-cell">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {schedule.duration} minutes
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="space-y-1">
                          {schedule.primarySubject && (
                            <div>
                              <span className="text-xs text-gray-500 font-medium">Primary:</span>
                              <span className={`badge text-xs ml-1 ${
                                schedule.primarySubject === 'abacus' ? 'badge-warning' :
                                schedule.primarySubject === 'math' ? 'badge-primary' :
                                schedule.primarySubject === 'english' ? 'badge-success' :
                                schedule.primarySubject === 'coding' ? 'badge-danger' :
                                'badge-secondary'
                              }`}>
                                {schedule.primarySubject.charAt(0).toUpperCase() + schedule.primarySubject.slice(1)}
                              </span>
                            </div>
                          )}
                          {schedule.subjects && schedule.subjects.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-500 font-medium">All Subjects:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {schedule.subjects.map((subject, index) => (
                                  <span key={index} className="badge badge-secondary text-xs">
                                    {subject}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {!schedule.primarySubject && (!schedule.subjects || schedule.subjects.length === 0) && (
                            <span className="badge badge-secondary text-xs">
                              No subjects
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {schedule.currentBookings}/{schedule.maxStudents}
                          </div>
                          <div className="text-xs text-gray-500">
                            {schedule.availableCapacity} available
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        {getAvailabilityBadge(schedule.isAvailable)}
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewSchedule(schedule)}
                            className="btn btn-sm btn-secondary"
                            title="View Schedule"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {user?.role === 'parent' ? (
                            <Link
                              to="/available-schedules"
                              className="btn btn-sm btn-primary"
                              title="Book Appointment"
                            >
                              <BookOpen className="h-4 w-4" />
                            </Link>
                          ) : canManageSchedule(schedule) && (                            <>
                              <button
                                onClick={() => handleEditSchedule(schedule)}
                                className="btn btn-sm btn-primary"
                                title="Edit Schedule"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteSchedule(schedule._id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Schedule"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Schedule Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Create New Schedule</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSchedule} className="modal-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher *
                  </label>
                  <select
                    name="teacher"
                    value={createFormData.teacher}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Teacher</option>
                    {getRoleSpecificUsers('teacher').map(teacher => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.firstName} {teacher.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Day of Week *
                  </label>
                  <select
                    name="dayOfWeek"
                    value={createFormData.dayOfWeek}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    required
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={createFormData.startTime}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time *
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={createFormData.endTime}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Type *
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="true"
                        checked={createFormData.isRecurring === true}
                        onChange={(e) => setCreateFormData(prev => ({ ...prev, isRecurring: true, oneTimeDates: [] }))}
                        className="mr-2"
                        required
                      />
                      <span className="text-sm font-medium text-gray-700">Recurring Schedule *</span>
                    </label>
                    {createFormData.isRecurring && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Recurring Duration *
                        </label>
                        <select
                          name="recurringDuration"
                          value={createFormData.recurringDuration}
                          onChange={(e) => handleInputChange(e, 'create')}
                          className="input w-full"
                          required={createFormData.isRecurring === true}
                        >
                          <option value="">Select Duration</option>
                          <option value="2weeks">2 Weeks</option>
                          <option value="1month">1 Month</option>
                          <option value="2months">2 Months</option>
                          <option value="3months">3 Months</option>
                          <option value="6months">6 Months</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Schedule will repeat every {createFormData.dayOfWeek} until the selected duration
                        </p>
                      </div>
                    )}
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="false"
                        checked={createFormData.isRecurring === false}
                        onChange={(e) => setCreateFormData(prev => ({ ...prev, isRecurring: false, recurringDuration: '2weeks' }))}
                        className="mr-2"
                        required
                      />
                      <span className="text-sm font-medium text-gray-700">One-Time Schedule *</span>
                    </label>
                    {!createFormData.isRecurring && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Specific Dates *
                        </label>
                        <div className="space-y-2">
                          {createFormData.oneTimeDates.map((date, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="date"
                                value={date}
                                onChange={(e) => {
                                  const newDates = [...createFormData.oneTimeDates];
                                  newDates[index] = e.target.value;
                                  setCreateFormData(prev => ({ ...prev, oneTimeDates: newDates }));
                                }}
                                className="input flex-1"
                                required={!createFormData.isRecurring && createFormData.oneTimeDates.length > 0}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newDates = createFormData.oneTimeDates.filter((_, i) => i !== index);
                                  setCreateFormData(prev => ({ ...prev, oneTimeDates: newDates }));
                                }}
                                className="btn btn-sm btn-danger"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setCreateFormData(prev => ({ 
                                ...prev, 
                                oneTimeDates: [...prev.oneTimeDates, ''] 
                              }));
                            }}
                            className="btn btn-sm btn-secondary w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Date
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Schedule will only occur on the selected dates
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Duration:</strong> Will be calculated automatically from start and end times
                    </p>
                    {createFormData.startTime && createFormData.endTime && (
                      <p className="text-xs text-blue-600 mt-1">
                        Current duration: {(() => {
                          const [startHour, startMin] = createFormData.startTime.split(':').map(Number);
                          const [endHour, endMin] = createFormData.endTime.split(':').map(Number);
                          const startMinutes = startHour * 60 + startMin;
                          const endMinutes = endHour * 60 + endMin;
                          const duration = endMinutes - startMinutes;
                          return duration > 0 ? `${duration} minutes` : 'Invalid (end time must be after start time)';
                        })()}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Students *
                  </label>
                  <input
                    type="number"
                    name="maxStudents"
                    value={createFormData.maxStudents}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <select
                    name="primarySubject"
                    value={createFormData.primarySubject}
                    onChange={(e) => handleInputChange(e, 'create')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="abacus">Abacus</option>
                    <option value="math">Math</option>
                    <option value="english">English</option>
                    <option value="coding">Coding</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={createFormData.isAvailable}
                      onChange={(e) => handleInputChange(e, 'create')}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Available</span>
                  </label>
                </div>
              </div>
            </form>
            <div className="modal-footer">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSchedule}
                className="btn btn-primary"
              >
                Create Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Schedule Modal */}
      {showViewModal && selectedSchedule && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Schedule Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Teacher</label>
                  <p className="text-sm text-gray-900">
                    {selectedSchedule.teacher?.firstName} {selectedSchedule.teacher?.lastName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Day</label>
                  <p className="text-sm text-gray-900">{getDayBadge(selectedSchedule.dayOfWeek)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Time</label>
                  <p className="text-sm text-gray-900">
                    {selectedSchedule.startTime} - {selectedSchedule.endTime} ({selectedSchedule.duration} minutes)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject Information</label>
                  <div className="space-y-2">
                    {selectedSchedule.primarySubject && (
                      <div>
                        <span className="text-xs text-gray-500 font-medium">Primary Subject:</span>
                        <div className="mt-1">
                          <span className={`badge text-sm ${
                            selectedSchedule.primarySubject === 'abacus' ? 'badge-warning' :
                            selectedSchedule.primarySubject === 'math' ? 'badge-primary' :
                            selectedSchedule.primarySubject === 'english' ? 'badge-success' :
                            selectedSchedule.primarySubject === 'coding' ? 'badge-danger' :
                            'badge-secondary'
                          }`}>
                            {selectedSchedule.primarySubject.charAt(0).toUpperCase() + selectedSchedule.primarySubject.slice(1)}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedSchedule.subjects && selectedSchedule.subjects.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500 font-medium">All Subjects:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedSchedule.subjects.map((subject, index) => (
                            <span key={index} className="badge badge-secondary text-xs">
                              {subject}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {!selectedSchedule.primarySubject && (!selectedSchedule.subjects || selectedSchedule.subjects.length === 0) && (
                      <span className="text-sm text-gray-500 italic">No subjects assigned</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-sm text-gray-900">
                    {selectedSchedule.duration || (() => {
                      if (selectedSchedule.startTime && selectedSchedule.endTime) {
                        const [startHour, startMin] = selectedSchedule.startTime.split(':').map(Number);
                        const [endHour, endMin] = selectedSchedule.endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;
                        return `${endMinutes - startMinutes} minutes`;
                      }
                      return 'N/A';
                    })()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Schedule Type</label>
                  <p className="text-sm text-gray-900">
                    {selectedSchedule.isRecurring !== false ? (
                      <>
                        <span className="badge badge-primary">Recurring</span>
                        {selectedSchedule.effectiveUntil && (
                          <span className="text-xs text-gray-500 ml-2">
                            Until {new Date(selectedSchedule.effectiveUntil).toLocaleDateString()}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="badge badge-secondary">One-Time</span>
                        {selectedSchedule.specialDates && selectedSchedule.specialDates.length > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            {selectedSchedule.specialDates.length} specific date(s)
                          </div>
                        )}
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Capacity</label>
                  <p className="text-sm text-gray-900">
                    {selectedSchedule.currentBookings}/{selectedSchedule.maxStudents} students
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="text-sm text-gray-900">{getAvailabilityBadge(selectedSchedule.isAvailable)}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {showEditModal && selectedSchedule && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Edit Schedule</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateSchedule} className="modal-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher
                  </label>
                  <select
                    name="teacher"
                    value={editFormData.teacher}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Teacher</option>
                    {getRoleSpecificUsers('teacher').map(teacher => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.firstName} {teacher.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Day of Week
                  </label>
                  <select
                    name="dayOfWeek"
                    value={editFormData.dayOfWeek}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={editFormData.startTime}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={editFormData.endTime}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Duration:</strong> Calculated automatically from start and end times
                    </p>
                    {editFormData.startTime && editFormData.endTime && (
                      <p className="text-xs text-blue-600 mt-1">
                        Current duration: {(() => {
                          const [startHour, startMin] = editFormData.startTime.split(':').map(Number);
                          const [endHour, endMin] = editFormData.endTime.split(':').map(Number);
                          const startMinutes = startHour * 60 + startMin;
                          const endMinutes = endHour * 60 + endMin;
                          const duration = endMinutes - startMinutes;
                          return duration > 0 ? `${duration} minutes` : 'Invalid (end time must be after start time)';
                        })()}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Students
                  </label>
                  <input
                    type="number"
                    name="maxStudents"
                    value={editFormData.maxStudents}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <select
                    name="primarySubject"
                    value={editFormData.primarySubject}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="abacus">Abacus</option>
                    <option value="math">Math</option>
                    <option value="english">English</option>
                    <option value="coding">Coding</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Type
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="true"
                        checked={editFormData.isRecurring === true || editFormData.isRecurring === undefined}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, isRecurring: true, oneTimeDates: [] }))}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Recurring Schedule</span>
                    </label>
                    {(editFormData.isRecurring === true || editFormData.isRecurring === undefined) && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Recurring Duration
                        </label>
                        <select
                          name="recurringDuration"
                          value={editFormData.recurringDuration || '2weeks'}
                          onChange={(e) => handleInputChange(e, 'edit')}
                          className="input w-full"
                        >
                          <option value="2weeks">2 Weeks</option>
                          <option value="1month">1 Month</option>
                          <option value="2months">2 Months</option>
                          <option value="3months">3 Months</option>
                          <option value="6months">6 Months</option>
                        </select>
                      </div>
                    )}
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isRecurring"
                        value="false"
                        checked={editFormData.isRecurring === false}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, isRecurring: false, recurringDuration: '2weeks' }))}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">One-Time Schedule</span>
                    </label>
                    {editFormData.isRecurring === false && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Specific Dates
                        </label>
                        <div className="space-y-2">
                          {editFormData.oneTimeDates?.map((date, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="date"
                                value={date}
                                onChange={(e) => {
                                  const newDates = [...(editFormData.oneTimeDates || [])];
                                  newDates[index] = e.target.value;
                                  setEditFormData(prev => ({ ...prev, oneTimeDates: newDates }));
                                }}
                                className="input flex-1"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newDates = (editFormData.oneTimeDates || []).filter((_, i) => i !== index);
                                  setEditFormData(prev => ({ ...prev, oneTimeDates: newDates }));
                                }}
                                className="btn btn-sm btn-danger"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setEditFormData(prev => ({ 
                                ...prev, 
                                oneTimeDates: [...(prev.oneTimeDates || []), ''] 
                              }));
                            }}
                            className="btn btn-sm btn-secondary w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Date
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={editFormData.isAvailable}
                      onChange={(e) => handleInputChange(e, 'edit')}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Available</span>
                  </label>
                </div>
              </div>
            </form>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSchedule}
                className="btn btn-primary"
              >
                Update Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;
